"use client";

import type { AuthMessage, AuthTone } from "@/lib/auth-errors";
import { cn } from "@/lib/utils";
import { AlertCircle, Check, CheckCircle2, Copy, Info, TriangleAlert } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

const TONE: Record<AuthTone, { box: string; icon: string; Icon: typeof Info }> = {
  error: { box: "border-destructive/30 bg-destructive/5", icon: "text-destructive", Icon: AlertCircle },
  warning: { box: "border-warning/40 bg-warning/10", icon: "text-warning", Icon: TriangleAlert },
  info: { box: "border-info/30 bg-info/5", icon: "text-info", Icon: Info },
  success: { box: "border-success/30 bg-success/5", icon: "text-success", Icon: CheckCircle2 },
};

/**
 * Giriş ekranındaki tüm hata/uyarı/bilgi mesajları (5 Eki 2026). Kod ve ref
 * varsa altta gösterilir; destek ekibi bunlarla kaydı bulur
 * (demcas docs/login-errors.md).
 */
export default function AuthNotice({ notice, className, children }: { notice: AuthMessage; className?: string; children?: React.ReactNode }) {
  const t = useTranslations("auth");
  const [copied, setCopied] = useState(false);
  const tone = TONE[notice.tone];
  const meta = [notice.code ? `${t("noticeCode")} ${notice.code}` : null, notice.ref ? `${t("noticeRef")} ${notice.ref}` : null].filter(Boolean).join(" · ");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(meta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // pano izni yoksa sessiz geç
    }
  };

  return (
    <div role={notice.tone === "error" ? "alert" : "status"} aria-live="polite" className={cn("flex gap-3 rounded-md border p-3.5", tone.box, className)}>
      <tone.Icon className={cn("mt-0.5 size-4 shrink-0", tone.icon)} aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-semibold leading-snug text-foreground">{notice.title}</p>
        {notice.message && <p className="text-sm leading-snug text-muted-foreground">{notice.message}</p>}
        {children}
        {meta && (
          <button
            type="button"
            onClick={copy}
            className="group mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-sm text-left font-mono text-[11px] text-muted-foreground/80 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t("noticeCopy")}
          >
            <span className="truncate">{meta}</span>
            {copied ? <Check className="size-3 shrink-0 text-success" /> : <Copy className="size-3 shrink-0 opacity-60 group-hover:opacity-100" />}
          </button>
        )}
      </div>
    </div>
  );
}
